import React, { useEffect, useState } from 'react';
import type { FosterHome, FosterPlacementRecord } from '../../lib/fosterAdapters';
import { formatDate, formatPhoneNumber } from '../../lib/utils';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import InfoTip, { HoverTip } from '../../components/InfoTip/InfoTip';
import { SETUP_STATUS_TOOLTIP_HOME } from './setupStatus';
import styles from './Devices.module.scss';

interface FosterHomeDetailsModalProps {
  open: boolean;
  onClose: () => void;
  home: FosterHome | null;
  onArchive: (home: FosterHome) => Promise<void>;
  /**
   * Placement ids whose pet the rescue can still open. A placement carries only the pet's name as
   * of the stay — never an id — so the parent resolves what it can and the rest stay plain text.
   */
  viewablePlacementIds?: Set<string>;
  /** The placement whose pet is being fetched, if any — opening one is a round trip. */
  loadingPlacementId?: string | null;
  onViewPlacementPet?: (placement: FosterPlacementRecord) => void;
}

const EM_DASH = '—';

/** Two letters for the removal dialog's avatar. */
function initials(home: FosterHome): string {
  return [home.firstName, home.lastName]
    .filter((part): part is string => !!part)
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

function plural(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

/**
 * An open placement is Active; a closed one reads by how it ended. UNKNOWN is its own state on
 * purpose — the ledger noticed the pet had left rather than being told, so `recalledAt` is when we
 * noticed, and the chip shouldn't imply we know the pet came back.
 */
function placementBadge(placement: FosterPlacementRecord): {
  variant: 'success' | 'info' | 'neutral' | 'warning';
  label: string;
} {
  if (!placement.recalledAt) {
    return { variant: 'success', label: 'Active' };
  }
  if (placement.endedReason === 'ADOPTED') {
    return { variant: 'info', label: 'Adopted' };
  }
  if (placement.endedReason === 'UNKNOWN') {
    return { variant: 'warning', label: 'Unknown' };
  }
  return { variant: 'neutral', label: 'Returned' };
}

/**
 * A saved home: the contact snapshot, what the household has set up, and who belongs to it.
 *
 * Removal tears the household down on top of retiring the record, so it takes a confirm step and is
 * offered only for an empty home — the backend rejects an occupied one regardless.
 */
export default function FosterHomeDetailsModal({
  open,
  onClose,
  home,
  onArchive,
  viewablePlacementIds,
  loadingPlacementId,
  onViewPlacementPet,
}: FosterHomeDetailsModalProps) {
  const [removing, setRemoving] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One modal instance serves every row, so reset on each open — `removing` included, or a request
  // that never settles leaves Remove disabled on every home the modal opens afterwards.
  //
  // Keyed on the id rather than the home object: the roster query runs cache-and-network, so every
  // refetch hands back an equal-but-new home, and keying on identity would reset mid-flow and
  // discard the confirm step the rescue is standing on. Keyed on `open` too, because "Remove from
  // roster" is a live destructive step — every path that closes the modal without changing `home`
  // (Escape, the backdrop, the page dropping the overlay on a URL change) would otherwise leave it
  // armed for the next opening of that same home.
  useEffect(() => {
    setConfirmingRemove(false);
    setRemoving(false);
    setError(null);
  }, [home?.id, open]);

  if (!home) {
    return null;
  }

  const currentHome = home;

  function handleClose() {
    if (removing) {
      return;
    }
    onClose();
  }

  async function handleRemove() {
    if (removing) {
      return;
    }
    setRemoving(true);
    setError(null);
    try {
      await onArchive(currentHome);
      onClose();
    } catch (err) {
      // Stay on the confirm step. The failure belongs next to the button that caused it — dropping
      // back to the details view puts it under the placement history, far from the action, and
      // silently discards the confirm state the rescue would need to retry.
      setError(err instanceof Error ? err.message : 'Could not remove this foster.');
    } finally {
      setRemoving(false);
    }
  }

  if (confirmingRemove) {
    return (
      <Modal open={open} onClose={handleClose}>
        <ModalTitle>Remove {currentHome.fullName}?</ModalTitle>
        <ModalSubtitle>
          This can&apos;t be undone. {currentHome.firstName} comes off your roster for good. Their placement record is
          your rescue&apos;s history, so that stays — adding them back later restores the count.
        </ModalSubtitle>

        <div className={styles.entityCard}>
          <div className={styles.entityAvatar}>{initials(currentHome)}</div>
          <div className={styles.entityInfo}>
            <div className={styles.entityName}>{currentHome.fullName}</div>
            <div className={styles.entityDetail}>
              {[
                currentHome.email,
                currentHome.phone ? formatPhoneNumber(currentHome.phone) : null,
                `${plural(currentHome.placementCount, 'placement')} lifetime`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        </div>

        {error && (
          <p className={styles.modalError} role="alert">
            {error}
          </p>
        )}

        <ModalActions>
          <Button variant="ghost" onClick={() => setConfirmingRemove(false)} disabled={removing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleRemove} disabled={removing}>
            {removing ? 'Removing…' : 'Remove Foster'}
          </Button>
        </ModalActions>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>{currentHome.fullName}</ModalTitle>
      <ModalSubtitle>
        Saved to your roster {formatDate(currentHome.createdAt)} · {plural(currentHome.placementCount, 'placement')}{' '}
        lifetime
      </ModalSubtitle>

      <div className={styles.subHead}>Contact</div>
      <div className={styles.detailRow}>
        <span className={styles.detailLabel}>Email</span>
        <span className={styles.detailValue}>{currentHome.email ?? EM_DASH}</span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.detailLabel}>Phone</span>
        <span className={styles.detailValue}>{currentHome.phone ? formatPhoneNumber(currentHome.phone) : EM_DASH}</span>
      </div>

      <div className={styles.subHead}>Foster household</div>
      <div className={styles.detailRow}>
        <span className={styles.detailLabel}>
          Setup
          {/* Stands in for the Setup column header's InfoTip: the badges below are hover-only,
              so this is the keyboard and screen-reader route to the same explanation. */}
          <InfoTip label="About the Setup badges">{SETUP_STATUS_TOOLTIP_HOME}</InfoTip>
        </span>
        <span className={styles.detailValue}>
          <HoverTip className={styles.pills} tip={SETUP_STATUS_TOOLTIP_HOME}>
            <StatusBadge variant={currentHome.hasSafeZone ? 'success' : 'error'}>Safe Zone</StatusBadge>
            <StatusBadge variant={currentHome.hasWifiSetup ? 'success' : 'error'}>WiFi</StatusBadge>
          </HoverTip>
        </span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.detailLabel}>Owners</span>
        <span className={styles.detailValue}>{currentHome.owners.join(' · ') || EM_DASH}</span>
      </div>
      <div className={styles.detailRow}>
        <span className={styles.detailLabel}>Caretakers</span>
        <span className={styles.detailValue}>{currentHome.caretakers.join(' · ') || EM_DASH}</span>
      </div>
      <div className={styles.subHead}>Placements</div>
      {currentHome.placements.length === 0 ? (
        <div className={styles.histEmpty}>No placements yet.</div>
      ) : (
        <div className={styles.hist}>
          {currentHome.placements.map((placement) => {
            const badge = placementBadge(placement);
            const canViewPet = !!onViewPlacementPet && !!viewablePlacementIds?.has(placement.id);
            return (
              <div key={placement.id} className={styles.histRow}>
                {canViewPet ? (
                  <button
                    type="button"
                    className={`${styles.histPet} ${styles.histPetButton}`}
                    // Opening is a fetch now, so the row says it is working. Disabled with it: a
                    // second click would be dropped in favour of the first, which from the outside
                    // is indistinguishable from the click doing nothing.
                    disabled={!!loadingPlacementId}
                    aria-busy={loadingPlacementId === placement.id}
                    onClick={() => onViewPlacementPet?.(placement)}
                  >
                    {placement.petName}
                    {loadingPlacementId === placement.id && <span className={styles.histPetSpinner} aria-hidden />}
                  </button>
                ) : (
                  <span className={styles.histPet}>{placement.petName}</span>
                )}
                <span className={styles.histDetail}>
                  {formatDate(placement.fosteredAt)} —{' '}
                  {placement.recalledAt ? formatDate(placement.recalledAt) : 'present'}
                </span>
                <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <p className={styles.modalError} role="alert">
          {error}
        </p>
      )}

      <ModalActions>
        <Button
          variant="danger"
          size="sm"
          onClick={() => setConfirmingRemove(true)}
          disabled={!currentHome.canArchive || removing}
        >
          Remove from roster
        </Button>
      </ModalActions>
      {!currentHome.canArchive && (
        <div className={styles.footHint}>Recall {currentHome.firstName}&apos;s devices to enable removal</div>
      )}
    </Modal>
  );
}
